import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import Controls, { InitialControlsDisplay } from "../../../session/editor/moduleControls/controls";
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { AboutLayoutTypesClassic, TemplateClassNames } from '../../../../../../types/template-layouts';
import { AboutContent, ColorKeys, ContentEditors, TextOverrides } from '../../../../../../types/working-model';
import { getDefaultModuleAlignment, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { BasicLangingModuleProps } from '../editor-main';
import '../../../../../../scss/live-event/base/modules/about-us.scss';
import './about.scss';
import { EUploadOptions, ImageUploader, ImageUploadOptions } from '../../../../../general-ui/image-uploader/image-uploader';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';

const EditorAbout: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const {
		AuthReducer: { token, user },
	} = useSelector((event: EventsState) => event);
	const contentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const dispatch = useDispatch();
	const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);

	const content: AboutContent = page_module.content;
	const styling_overrides = page_module?.styling_overrides;
	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);
	const layout_type = page_module.content?.layout_type || AboutLayoutTypesClassic.standard;

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage,
							language
						}),
					},
					...(key === 'description' && { is_edited: true }),
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		if (!token) return;
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...page_module.content,
					originalImage: image.original,
					image: image.edited,
				}
			})
		);
		// ASSISTANT CHECKLIST ITEM: add assisstant progress checklist item for editing the about section
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editAbout)
		) {
			dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editAbout));
		}
	}, [dispatch, page_module, token, user]);

	// TODO: implement image gallery for about post MVP
	const imageLength = 1;
	const renderGallery = () => {
		const gallery = [];
		for (let i = 0; i < imageLength; i += 1) {
			gallery.push(<img key={i} src={content?.image} alt="" aria-hidden="true" width="275" height="220" />);
		}
		return gallery;
	};

	const { custom_heading, description, title } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	const controlsDisplay: InitialControlsDisplay = {
		changeSectionPosition: true,
		editColors: true,
		editLayout: true
	};

	return page_module.is_on ? (
		<section
			ref={ref}
			className={classNames(
				'editor-wrapper about-us',
				`about-us-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				module={page_module}
				display={controlsDisplay}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="about-us-text">
					<EditableTextSlim
						className="about-us-custom-heading"
						tagType="p"
						value={t(`${namespace}:about.${page_module.id}.heading`, t(`${namespace}:about.heading`, custom_heading))}
						onChange={handleTextChange('custom_heading')}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
						defaultColor={customHeadingDefaultColor}
						elementName={EContentColorOverrideNames.custom_heading}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
					/>
					<div className="about-us-text-inner">
						<EditableTextSlim
							className="about-us-title"
							tagType="h2"
							value={t(`${namespace}:about.${page_module.id}.title`, t(`${namespace}:about.title`, title))}
							onChange={handleTextChange('title')}
							colorOverride={styling_overrides?.content?.title?.color}
							defaultColor={ColorKeys.bodyTextColor}
							elementName={EContentColorOverrideNames.title}
							onOverrideChange={handleStyleOverrideChange('title')}
						/>
						<EditableTextDraftQuill
							className="about-body-text evt-body-text-1"
							value={t(`${namespace}:about.${page_module.id}.description`, t(`${namespace}:about.description`, description))}
							onChange={handleTextChange('description')}
							styleOverrides={styling_overrides?.content?.description}
							defaultColor={ColorKeys.bodyTextColor}
							elementName={EContentColorOverrideNames.description}
							onOverrideChange={handleStyleOverrideChange('description')}
							useNew={contentEditor === ContentEditors.quill}
							defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
						/>
					</div>
				</div>
				<div className={classNames('about-us-gallery-wrapper', `about-us-gallery-wrapper-${imageLength}`)}>
					<div className={classNames('about-us-gallery', `about-us-gallery-${imageLength}`)}>
						{renderGallery()}
					</div>
					{
						<div className='edit-button-container'>
							{content?.originalImage ? (
								<button className="round edit-button" onClick={() => setImageModalOpen(EUploadOptions.edit)}><Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} /></button>
							) : null}
							<button className="round edit-button replace-image" onClick={() => setImageModalOpen(EUploadOptions.upload)}>
								<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
							</button>
						</div>
					}
				</div>
			</div>

			<ImageUploader
				originalImage={content?.originalImage}
				onSave={saveImage}
				onClose={() => setImageModalOpen(null)}
				open={imageModalOpen}
			/>
		</section>
	) : null;
});

EditorAbout.displayName = 'EditorAbout';

export default EditorAbout;
